// src/pages/tournamentDetails/EliminationModal.jsx
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export default function EliminationModal({
  open,
  target,
  activeEntrants,
  elimById,
  setElimById,
  onCancel,
  onConfirm,
  submitting,
}) {
  const selectRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";

    const timer = window.setTimeout(() => selectRef.current?.focus(), 100);

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) onCancel?.();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
    };
  }, [open, submitting, onCancel]);

  if (!open || !target) return null;

  const targetName =
    target.displayName || target.name || target.email || target.uid || target.id || "Player";

  const modal = (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/75 px-3 py-3 sm:px-4 sm:py-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="elimination-modal-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onCancel?.();
      }}
    >
      <div className="w-full max-w-md max-h-[calc(100dvh-24px)] overflow-y-auto rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-900 px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 id="elimination-modal-title" className="text-xl font-bold text-[#B87333]">
                Record Elimination
              </h3>
              <p className="mt-1 text-sm text-zinc-400">This knockout will update the live field immediately.</p>
            </div>

            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-xl text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
              aria-label="Close elimination modal"
            >
              ×
            </button>
          </div>
        </div>

        <div className="px-5 py-5 sm:px-6">
          <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-red-300">Eliminated Player</p>
            <p className="mt-1 text-xl font-bold text-white">{targetName}</p>
          </div>

          <label className="mt-5 block text-sm font-semibold text-zinc-200">Eliminated by</label>
          <select
            ref={selectRef}
            className="mt-2 w-full rounded-xl border border-zinc-700 bg-zinc-950 p-4 text-base text-zinc-100 outline-none focus:border-[#B87333] focus:ring-2 focus:ring-[#B87333]/25"
            value={elimById}
            onChange={(event) => setElimById(event.target.value)}
            disabled={submitting}
          >
            <option value="">Select player...</option>
            {(Array.isArray(activeEntrants) ? activeEntrants : [])
              .filter((entrant) => entrant.id !== target.id)
              .map((entrant) => {
                const name =
                  entrant.displayName || entrant.name || entrant.email || entrant.uid || entrant.id;
                return (
                  <option key={entrant.id} value={entrant.id}>
                    {name}
                  </option>
                );
              })}
          </select>

          {!elimById ? (
            <p className="mt-2 text-sm text-zinc-500">Choose the active player who made the knockout.</p>
          ) : null}
        </div>

        <div className="sticky bottom-0 grid grid-cols-1 gap-3 border-t border-zinc-800 bg-zinc-900 px-5 py-4 sm:grid-cols-2 sm:px-6">
          <button
            type="button"
            onClick={onCancel}
            className="order-2 min-h-12 rounded-xl bg-zinc-800 px-4 py-3 font-semibold text-zinc-100 hover:bg-zinc-700 disabled:opacity-60 sm:order-1"
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            className="order-1 min-h-12 rounded-xl bg-red-600 px-4 py-3 font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-45 sm:order-2"
            disabled={submitting || !elimById}
            title={!elimById ? "Select who eliminated this player" : ""}
          >
            {submitting ? "Saving..." : "Confirm Elimination"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
